import { supabase, supabaseAnonKey, supabaseUrl } from './supabaseClient';
import type { PrimaryAnalysis } from '../types/primaryAnalysis';

type InventoryInput = {
  purchaseDate: string;
  purchasePrice: number;
  purchaseLocation: string;
  inventorySection: 'keep' | 'for_sale' | 'for-sale';
};

const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read the selected image.'));
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.readAsDataURL(file);
});

export async function saveSharedAnalysis(input: {
  clientAnalysisId: string;
  analysis: PrimaryAnalysis;
  image?: File;
  saveAnalysis?: boolean;
  inventory?: InventoryInput;
  sourceApp: 'web' | 'appdeploy';
}) {
  const image = input.image ? { data: await fileToBase64(input.image), mimeType: input.image.type || 'image/jpeg' } : undefined;
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session?.access_token) {
    throw new Error('Your session expired. Sign in again before saving this item.');
  }
  const response = await fetch(`${supabaseUrl}/functions/v1/thriftalyze-data`, {
    method: 'POST',
    headers: {
      apikey: supabaseAnonKey,
      authorization: `Bearer ${sessionData.session.access_token}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ action: 'save', ...input, image }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.error || `Inventory request failed (${response.status})`);
  if (data?.error) throw new Error(data.error);
  if (input.inventory && !data?.inventory) throw new Error('Inventory confirmation was not returned.');
  if (input.saveAnalysis && !data?.saved) throw new Error('History confirmation was not returned.');
  return data;
}

export async function resolveSharedImageUrl(value?: string | null) {
  if (!value || /^https?:\/\//i.test(value)) return value || '';
  const normalized = value.replace(/^storage:\/\//, '');
  const slash = normalized.indexOf('/');
  const bucket = slash > 0 ? normalized.slice(0, slash) : 'thriftalyze-items';
  const path = slash > 0 ? normalized.slice(slash + 1) : normalized;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
  if (error) return '';
  return data.signedUrl;
}
